import {allowedProvider, isAllowedProvider, ProviderInput} from "./embed.provider";
import {EmbedStorage} from "./storage/storage";
import {EmbedLocalStorage} from "./storage/localstorage.storage";
import {Logging} from "../logging";
import {EmbedCookieStorage} from "./storage/cookie.storage";

type VoidF = () => void;

export class EmbedManager {
    private static instance?: EmbedManager;
    #logger: Logging;
    #storage: EmbedStorage;
    #storageFallback: EmbedStorage;
    #listeners: VoidF[] = [];

    private constructor() {
        this.#storage = new EmbedLocalStorage();
        this.#storageFallback = new EmbedCookieStorage(allowedProvider);
        this.#logger = new Logging('Embed', 'background: #ffb300; color: white;');
    }

    static getInstance() {
        if (!EmbedManager.instance) EmbedManager.instance = new EmbedManager();
        return EmbedManager.instance;
    }

    subscribeToChanges(callback: VoidF) {
        this.#listeners.push(callback);
    }

    providerHasConsent(provider: ProviderInput): boolean {
        if (!isAllowedProvider(provider)) {
            this.#logger.error(`Unknown embed: ${provider}`);
            return false;
        }
        // If non-consent state but fallback would have consent, we transfer consent state
        if (!this.#storage.providerHasConsent(provider) && this.#storageFallback.providerHasConsent(provider)) {
            this.#storage.setProviderConsent(provider, true);
            this.#logger.info(`Fallback consent found for: ${provider}`);
        }
        return this.#storage.providerHasConsent(provider);
    }

    /**
     * Forwards provider consent status to storage and notifies all listeners.
     * @param provider
     * @param allowed
     */
    setProviderConsent(provider: ProviderInput, allowed: boolean): void {
        if (!isAllowedProvider(provider)) {
            this.#logger.error(`Unknown embed: ${provider}`);
            return;
        }
        this.#storage.setProviderConsent(provider, allowed);
        this.notifyListeners().then(() => !allowed && window.location.reload());
        this.#logger.info(`Updated embed consent for "${provider}": ${allowed}`);
    }

    /**
     * Reset all consent states and reload page. Reload is necessary as there is no way of
     * knowing what a loaded script might have done/loaded.
     */
    resetConsent(): void {
        this.#storage.resetConsent();
        this.notifyListeners().then(() => window.location.reload());
        this.#logger.info('Reset embed consent');
    }

    /**
     * Call all listeners while releasing control back to the browser after each to ensure no uninterrupted
     * long-running tasks.
     * @private
     */
    private async notifyListeners() {
        for (const callback of this.#listeners) {
            await scheduler.yield();
            await scheduler.postTask(callback, {priority: 'user-visible'});
        }
    }
}
